import styled, { css } from 'styled-components'

import { thinScrollbar } from '@/theme/common'

export const Container = styled.div(({ theme }) => ({
  backgroundColor: theme.colors.white,
  borderRadius: theme.border.radius['8'],
  display: 'flex',
  flexDirection: 'column',
  fontFamily: 'inherit',
  userSelect: 'none',
  width: '27.8rem',
}))

export const Header = styled.div(({ theme }) => ({
  alignItems: 'center',
  background: theme.colors.gray100,
  display: 'flex',
  gap: theme.space['12'],
  justifyContent: 'space-between',
  padding: `${theme.space['12']} ${theme.space['8']}`,
}))

export const MonthYear = styled.div(({ theme }) => ({
  color: theme.colors.black,
  fontSize: '1.2rem',
  fontWeight: 600,
  lineHeight: '1.6rem',
  textAlign: 'center',
}))

export const SelectContainer = styled.div(({ theme }) => ({
  // Note: SelectTrigger/PeriodButton carry horizontal padding so their open-state
  // background reads as a chip, but that padding shouldn't widen the gap between
  // adjacent triggers — cancel the facing sides with a matching negative margin so
  // the visible gap stays exactly `gap` regardless of that padding.
  '& > button:not(:first-child)': {
    marginLeft: `-${theme.space['12']}`,
  },

  '& > button:not(:last-child)': {
    marginRight: `-${theme.space['12']}`,
  },
  alignItems: 'center',
  display: 'flex',
  gap: theme.space['14'],
}))

export const SelectTrigger = styled.button<{ $isOpen: boolean }>(({ theme, $isOpen }) => ({
  '& svg': {
    transform: $isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
    transition: `transform ${theme.transition.duration.sm} ease`,
  },

  '&:hover': {
    opacity: 0.7,
  },

  alignItems: 'center',
  background: $isOpen ? theme.colors.primary200 : 'transparent',
  border: 'none',
  borderRadius: theme.border.radius['4'],
  color: theme.colors.primary800,
  cursor: 'pointer',
  display: 'flex',
  fontSize: '1.2rem',
  fontWeight: 600,
  gap: theme.space['4'],
  lineHeight: '1.6rem',
  padding: `${theme.space['4']} ${theme.space['12']}`,
  transition: `background-color ${theme.transition.duration.sm} ease, opacity ${theme.transition.duration.sm} ease`,
}))

export const PeriodButton = styled.button<{ $isOpen: boolean }>(({ theme, $isOpen }) => ({
  '&:hover': {
    opacity: 0.7,
  },

  alignItems: 'center',
  background: $isOpen ? theme.colors.primary200 : 'transparent',
  border: 'none',
  borderRadius: theme.border.radius['4'],
  color: theme.colors.primary800,
  cursor: 'pointer',
  display: 'flex',
  fontSize: '1.2rem',
  fontWeight: 600,
  gap: theme.space['4'],
  lineHeight: '1.6rem',
  padding: `${theme.space['4']} ${theme.space['12']}`,
  transition: `background-color ${theme.transition.duration.sm} ease, opacity ${theme.transition.duration.sm} ease`,
}))

export const SelectDropdown = styled.div(
  ({ theme }) => css`
    ${thinScrollbar};
    background-color: ${theme.colors.white};
    border: ${theme.border.size['1']} solid ${theme.colors.gray300};
    border-radius: ${theme.border.radius['4']};
    box-shadow: ${theme.shadow.md};
    max-height: 20rem;
    min-width: 12rem;
    overflow-y: auto;
    width: auto;
    /* Note: Must be higher than date-picker-input's Dropdown (level3) to appear on top */
    z-index: ${theme.zIndex.level4};
  `,
)

export const SelectOption = styled.button<{ $isSelected: boolean }>(({ theme, $isSelected }) => ({
  '&:hover': {
    backgroundColor: theme.colors.gray200,
  },

  backgroundColor: $isSelected ? theme.colors.gray100 : theme.colors.white,
  border: 'none',
  color: theme.colors.black,
  cursor: 'pointer',
  display: 'block',
  fontSize: '1.2rem',
  fontWeight: $isSelected ? 600 : 500,
  lineHeight: '1.6rem',
  padding: `${theme.space['8']} ${theme.space['12']}`,
  textAlign: 'left',
  transition: `background-color ${theme.transition.duration.sm} ease`,
  width: '100%',
}))

export const DaysGrid = styled.div(({ theme }) => ({
  display: 'grid',
  gap: `${theme.space['4']} 0`,
  gridTemplateColumns: 'repeat(7, 1fr)',
}))

export const DayLabel = styled.div(({ theme }) => ({
  alignItems: 'center',
  background: theme.colors.gray100,
  color: theme.colors.black,
  display: 'flex',
  fontSize: '1.2rem',
  fontWeight: 600,
  justifyContent: 'center',
  lineHeight: '1.6rem',
  minHeight: '2.8rem',
  textAlign: 'center',
}))

export const DayCell = styled.button<{
  $isCurrentMonth: boolean
  $isSelected: boolean
  $isDisabled: boolean
  $isInRange: boolean
  $isToday: boolean
}>(({ theme, $isCurrentMonth, $isSelected, $isDisabled, $isInRange, $isToday }) => {
  const getBackground = () => {
    if ($isSelected) return theme.colors.black800
    if ($isInRange) return theme.colors.gray200
    return 'transparent'
  }

  const getHoverBackground = () => {
    if ($isSelected) return theme.colors.primary600
    if ($isDisabled) return 'transparent'
    return theme.colors.gray300
  }

  const getBorder = () => {
    const canHighlight = $isToday && !$isSelected && !$isInRange
    return `${theme.border.size['1']} solid ${canHighlight ? theme.colors.primary800 : 'transparent'}`
  }

  const getBorderRadius = () => {
    if ($isSelected) return theme.border.radius['4']
    if ($isInRange) return theme.border.radius['0']
    return theme.border.radius['4']
  }

  const getColor = () => {
    if ($isSelected) return theme.colors.white
    if ($isCurrentMonth) return theme.colors.black
    return theme.colors.black200
  }

  const round = theme.border.radius['4']
  const square = theme.border.radius['0']

  return {
    '&:hover': {
      backgroundColor: getHoverBackground(),
    },

    // Note: A range endpoint only rounds its outer corner — whichever neighboring
    // sibling (in DOM/date order) is also in-range tells us which side faces into
    // the range, so that corner stays square, like a continuous pill.
    '&[data-in-range="true"] + &[data-selected="true"]': {
      borderRadius: `${square} ${round} ${round} ${square}`,
    },

    '&[data-selected="true"]:has(+ &[data-in-range="true"])': {
      borderRadius: `${round} ${square} ${square} ${round}`,
    },

    alignItems: 'center',
    aspectRatio: '1',
    background: getBackground(),
    border: getBorder(),
    borderRadius: getBorderRadius(),
    color: getColor(),
    cursor: $isDisabled ? 'not-allowed' : 'pointer',
    display: 'flex',
    fontSize: '1.2rem',
    fontWeight: 500,
    justifyContent: 'center',
    lineHeight: '1.6rem',
    minHeight: '2.8rem',
    minWidth: '2.8rem',
    opacity: $isDisabled ? 0.4 : 1,
    padding: theme.space['4'],
    transition: `all ${theme.transition.duration.sm} ease`,
  }
})
